/**
 * DSP-Bausteine der Capture-Pipeline (Doc 04 §2/§3, Doc 13 §3.1/§3.2).
 *
 * WICHTIG — diese Klassen werden per `Function.prototype.toString()` in den
 * AudioWorklet-Quelltext kopiert (siehe worklet.ts). Deshalb gilt:
 *   - jede Klasse ist vollständig selbsttragend: keine Imports, keine
 *     Modul-Helfer, keine Verweise auf andere Klassen dieses Moduls;
 *   - keine Klassenfeld-Initialisierer (`x = 1;`) — alles im Konstruktor;
 *   - nur Sprachmittel bis ES2020 + Typed Arrays.
 * Die Unit-Tests prüfen genau diese Klassen, und ein Test wertet den
 * generierten Worklet-Quelltext in einer Sandbox aus.
 */

/** Konfiguration des Energie-VAD (Nahfeld-lastig, siehe README „Entscheidungen"). */
export interface VadConfig {
  /** Abtastrate der Eingabe (nach Resampling) */
  sampleRate: number;
  /** Blocklänge für die RMS-Messung */
  blockMs: number;
  /** Sprache = RMS > max(minRms, Rauschboden × ratio) */
  ratio: number;
  /** absolute Untergrenze der Schwelle (lineares RMS, 0..1) */
  minRms: number;
  /** mindestens so viele Blöcke über der Schwelle, bevor Sprache startet */
  attackBlocks: number;
  /**
   * Sprache startet erst nach so viel zusammenhängender Sprache (ms vom ersten
   * lauten Block bis zum Ende des aktuellen lauten Blocks). Klicks, Klopfen,
   * Rascheln unter dieser Dauer öffnen das Gate nicht (Workshop 2026-10-08:
   * winzige Bursts → Prompt-Echo der ASR). Der Pre-Roll-Ring muss diese Zeit
   * zusätzlich abdecken (siehe `SpeechGate.preRollFramesFor`).
   */
  minSpeechMs: number;
  /** Lücken (unter der Schwelle) bis zu dieser Dauer unterbrechen die Mindestdauer nicht (Silben-/Wortpausen) */
  minSpeechGapMs: number;
  /** so lange unter der Schwelle beendet Sprache */
  hangoverMs: number;
  /** Startwert des Rauschbodens */
  initialFloor: number;
  /** Rauschboden = Minimum der Block-RMS über dieses Fenster (Minimum-Tracking) */
  floorWindowMs: number;
  /** Glättung beim Anstieg des Bodens (pro Block); Abfall wirkt sofort */
  floorRise: number;
}

export const DEFAULT_VAD_CONFIG: VadConfig = {
  sampleRate: 16000,
  blockMs: 20,
  ratio: 4, // +12 dB über Rauschboden
  minRms: 0.01, // ≈ −40 dBFS
  attackBlocks: 2, // 40 ms
  minSpeechMs: 250,
  minSpeechGapMs: 100,
  hangoverMs: 500,
  initialFloor: 0.003,
  floorWindowMs: 3000,
  floorRise: 0.1,
};

export type VadEvent = { type: 'start' | 'end'; sample: number };

/**
 * Streaming-Resampler: Butterworth-Tiefpass 4. Ordnung (zwei Biquads, nur
 * beim Heruntertasten, fc = 0,45 × Zielrate) + lineare Interpolation.
 * Zustand bleibt über Aufrufe erhalten → Blockgrenzen sind unsichtbar.
 */
export class LinearResampler {
  readonly inRate: number;
  readonly outRate: number;
  private step: number;
  private pos: number;
  private prev: number;
  private filter: boolean;
  private s: Float64Array;
  private c: Float64Array;

  constructor(inRate: number, outRate: number) {
    this.inRate = inRate;
    this.outRate = outRate;
    this.step = inRate / outRate;
    this.pos = 0;
    this.prev = 0;
    this.filter = inRate > outRate;
    // Biquad-Koeffizienten [b0,b1,b2,a1,a2] × 2 Stufen, Zustand [x1,x2,y1,y2] × 2
    this.c = new Float64Array(10);
    this.s = new Float64Array(8);
    if (this.filter) {
      const fc = 0.45 * outRate;
      const qs = [0.5411961, 1.3065630];
      for (let k = 0; k < 2; k++) {
        const w0 = (2 * Math.PI * fc) / inRate;
        const alpha = Math.sin(w0) / (2 * (qs[k] as number));
        const cw = Math.cos(w0);
        const a0 = 1 + alpha;
        this.c[k * 5 + 0] = (1 - cw) / 2 / a0;
        this.c[k * 5 + 1] = (1 - cw) / a0;
        this.c[k * 5 + 2] = (1 - cw) / 2 / a0;
        this.c[k * 5 + 3] = (-2 * cw) / a0;
        this.c[k * 5 + 4] = (1 - alpha) / a0;
      }
    }
  }

  /** Erwartete Ausgabelänge (±1) für n Eingabesamples. */
  static expectedLength(n: number, inRate: number, outRate: number): number {
    return Math.floor((n * outRate) / inRate);
  }

  process(input: Float32Array): Float32Array {
    const n = input.length;
    if (n === 0) return new Float32Array(0);
    let x: Float32Array = input;
    if (this.filter) {
      x = new Float32Array(n);
      const c = this.c;
      const s = this.s;
      for (let i = 0; i < n; i++) {
        let v = input[i] as number;
        for (let k = 0; k < 2; k++) {
          const o = k * 4;
          const p = k * 5;
          const y =
            (c[p] as number) * v + (c[p + 1] as number) * (s[o] as number) + (c[p + 2] as number) * (s[o + 1] as number) -
            (c[p + 3] as number) * (s[o + 2] as number) - (c[p + 4] as number) * (s[o + 3] as number);
          s[o + 1] = s[o] as number;
          s[o] = v;
          s[o + 3] = s[o + 2] as number;
          s[o + 2] = y;
          v = y;
        }
        x[i] = v;
      }
    }
    if (this.step === 1) return x;
    const cap = Math.ceil((n - this.pos) / this.step) + 2;
    const out = new Float32Array(Math.max(0, cap));
    let m = 0;
    let pos = this.pos;
    while (pos <= n - 1) {
      const i = Math.floor(pos);
      const f = pos - i;
      const a = i < 0 ? this.prev : (x[i] as number);
      const b = i + 1 < n ? (x[i + 1] as number) : a;
      out[m++] = a + (b - a) * f;
      pos += this.step;
    }
    this.pos = pos - n;
    this.prev = x[n - 1] as number;
    return out.subarray(0, m);
  }
}

/**
 * Float32 → Int16 (Clamping, kein Dither) und Zerlegung in feste Frames
 * (Standard 1280 Samples = 80 ms bei 16 kHz).
 */
export class Int16Framer {
  readonly frameSamples: number;
  private buf: Int16Array;
  private fill: number;
  /** Anzahl bisher aufgenommener echter Samples (Stream-Uhr). */
  total: number;

  constructor(frameSamples: number) {
    this.frameSamples = frameSamples;
    this.buf = new Int16Array(frameSamples);
    this.fill = 0;
    this.total = 0;
  }

  static toInt16(x: number): number {
    if (!(x === x)) return 0; // NaN
    const v = x > 1 ? 1 : x < -1 ? -1 : x;
    return v < 0 ? Math.round(v * 32768) : Math.round(v * 32767);
  }

  /** Liefert fertige Frames samt Start-Sample-Index. */
  push(input: Float32Array): Array<{ frame: Int16Array; start: number }> {
    const out: Array<{ frame: Int16Array; start: number }> = [];
    // über constructor statt Klassennamen: robust gegen Umbenennung beim Minifizieren
    const conv = (this.constructor as typeof Int16Framer).toInt16;
    for (let i = 0; i < input.length; i++) {
      this.buf[this.fill++] = conv(input[i] as number);
      this.total++;
      if (this.fill === this.frameSamples) {
        out.push({ frame: this.buf, start: this.total - this.frameSamples });
        this.buf = new Int16Array(this.frameSamples);
        this.fill = 0;
      }
    }
    return out;
  }

  /** Angefangenen Frame mit Nullen auffüllen und ausgeben (PTT-Loslassen). */
  flushPadded(): { frame: Int16Array; start: number } | null {
    if (this.fill === 0) return null;
    const frame = this.buf;
    const start = this.total - this.fill;
    this.buf = new Int16Array(this.frameSamples);
    this.fill = 0;
    return { frame, start };
  }

  /** Angefangenen Frame verwerfen. */
  reset(): void {
    this.fill = 0;
  }
}

/**
 * Energie-VAD: RMS über 20-ms-Blöcke, Schwelle relativ zum laufenden
 * Rauschboden, Start nach ≥ 250 ms zusammenhängender Sprache (Lücken ≤ 100 ms
 * zählen mit, Start-Sample = erster lauter Block), Hangover 500 ms. Reine
 * Zustandsmaschine.
 *
 * Rauschboden = Minimum der Block-RMS im gleitenden Fenster (3 s), nach
 * oben geglättet. Sprache hat zwischen Silben/Wörtern immer leise Blöcke,
 * also bleibt der Boden beim Sprechen unten; stationärer Lärm hebt ihn
 * binnen eines Fensters an und löst danach nicht mehr aus.
 */
export class EnergyVad {
  readonly cfg: VadConfig;
  readonly blockSamples: number;
  floor: number;
  active: boolean;
  private above: number;
  private gapSamples: number;
  private belowSamples: number;
  private blockSum: number;
  private blockFill: number;
  private sample: number;
  private firstAbove: number;
  private hist: Float32Array;
  private histFill: number;
  private histPos: number;

  constructor(cfg: VadConfig) {
    this.cfg = cfg;
    this.blockSamples = Math.max(1, Math.round((cfg.sampleRate * cfg.blockMs) / 1000));
    this.floor = cfg.initialFloor;
    this.active = false;
    this.above = 0;
    this.gapSamples = 0;
    this.belowSamples = 0;
    this.blockSum = 0;
    this.blockFill = 0;
    this.sample = 0;
    this.firstAbove = 0;
    this.hist = new Float32Array(Math.max(1, Math.round(cfg.floorWindowMs / cfg.blockMs)));
    this.histFill = 0;
    this.histPos = 0;
  }

  threshold(): number {
    return Math.max(this.cfg.minRms, this.floor * this.cfg.ratio);
  }

  /** Ein Block-RMS verarbeiten (Block endet bei `endSample`). */
  processBlock(rms: number, endSample: number): VadEvent | null {
    const thr = this.threshold();
    const isAbove = rms > thr;
    let ev: VadEvent | null = null;
    if (isAbove) {
      if (this.above === 0) this.firstAbove = endSample - this.blockSamples;
      this.above++;
      this.gapSamples = 0;
      this.belowSamples = 0;
      const spanMs = ((endSample - this.firstAbove) * 1000) / this.cfg.sampleRate;
      if (!this.active && this.above >= this.cfg.attackBlocks && spanMs >= this.cfg.minSpeechMs) {
        this.active = true;
        ev = { type: 'start', sample: this.firstAbove };
      }
    } else {
      if (!this.active && this.above > 0) {
        // Kandidat: kurze Lücken tolerieren, längere setzen die Mindestdauer zurück
        this.gapSamples += this.blockSamples;
        if ((this.gapSamples * 1000) / this.cfg.sampleRate > this.cfg.minSpeechGapMs) {
          this.above = 0;
          this.gapSamples = 0;
        }
      } else {
        this.above = 0;
      }
      if (this.active) {
        this.belowSamples += this.blockSamples;
        if ((this.belowSamples * 1000) / this.cfg.sampleRate >= this.cfg.hangoverMs) {
          this.active = false;
          this.belowSamples = 0;
          ev = { type: 'end', sample: endSample };
        }
      }
    }
    // Rauschboden: Minimum im Fenster, Anstieg geglättet, Abfall sofort
    this.hist[this.histPos] = rms;
    this.histPos = (this.histPos + 1) % this.hist.length;
    if (this.histFill < this.hist.length) this.histFill++;
    let min = Infinity;
    for (let i = 0; i < this.histFill; i++) {
      const v = this.hist[i] as number;
      if (v < min) min = v;
    }
    if (min < this.floor) this.floor = min;
    else this.floor += this.cfg.floorRise * (min - this.floor);
    if (this.floor < 1e-5) this.floor = 1e-5;
    return ev;
  }

  /**
   * Int16-Samples verarbeiten. Liefert, ob die VAD irgendwann in diesem
   * Abschnitt aktiv war, und die Zustandswechsel.
   */
  processInt16(samples: Int16Array): { activeAny: boolean; events: VadEvent[] } {
    const events: VadEvent[] = [];
    let activeAny = this.active;
    for (let i = 0; i < samples.length; i++) {
      const v = (samples[i] as number) / 32768;
      this.blockSum += v * v;
      this.blockFill++;
      this.sample++;
      if (this.blockFill === this.blockSamples) {
        const ev = this.processBlock(Math.sqrt(this.blockSum / this.blockFill), this.sample);
        this.blockSum = 0;
        this.blockFill = 0;
        if (ev) events.push(ev);
        if (this.active) activeAny = true;
      }
    }
    return { activeAny, events };
  }
}

export type GateAction =
  | { type: 'frame'; frame: Int16Array; start: number }
  | { type: 'speech'; state: 'start' | 'end'; sample: number };

/**
 * Sende-Gate: entscheidet pro Frame, ob er gesendet wird, und erzeugt die
 * `speech`-Grenzen.
 *   listener: Frames nur während VAD-Aktivität (inkl. Hangover), beim Start
 *             zuerst der Pre-Roll-Ringpuffer (ceil(preRollMs/frameMs) Frames).
 *   ptt:      Frames nur zwischen pttDown und pttUp; beim Loslassen wird der
 *             angefangene Frame (aufgefüllt) noch gesendet.
 */
export class SpeechGate {
  mode: 'listener' | 'ptt';
  open: boolean;
  pressed: boolean;
  readonly preRollFrames: number;
  private ring: Array<{ frame: Int16Array; start: number }>;
  private lastEnd: number;

  constructor(mode: 'listener' | 'ptt', preRollFrames: number) {
    this.mode = mode;
    this.preRollFrames = preRollFrames;
    this.open = false;
    this.pressed = false;
    this.ring = [];
    this.lastEnd = 0;
  }

  /**
   * Frames im Pre-Roll-Ring. Für den Listener `preRollMs + minSpeechMs +
   * minSpeechGapMs` übergeben: die VAD meldet den Start erst nach der
   * Mindestdauer, der Ring muss bis `preRollMs` vor den ersten lauten Block
   * zurückreichen.
   */
  static preRollFramesFor(preRollMs: number, frameSamples: number, sampleRate: number): number {
    return Math.ceil((preRollMs * sampleRate) / 1000 / frameSamples);
  }

  setMode(mode: 'listener' | 'ptt'): GateAction[] {
    const out: GateAction[] = [];
    if (mode !== this.mode && this.open) {
      this.open = false;
      out.push({ type: 'speech', state: 'end', sample: this.lastEnd });
    }
    this.mode = mode;
    this.pressed = false;
    this.ring = [];
    return out;
  }

  /**
   * `keepFrom` (optional, Listener): erstes Sample, das beim Öffnen noch
   * gebraucht wird (= Sprachbeginn laut VAD − Pre-Roll). Ältere Ring-Frames
   * werden nicht gesendet — der Ring ist für die Mindest-Sprechdauer größer
   * als der Pre-Roll, gesendet wird trotzdem nur der Pre-Roll.
   */
  onFrame(frame: Int16Array, start: number, vadActive: boolean, keepFrom?: number): GateAction[] {
    const end = start + frame.length;
    const out: GateAction[] = [];
    if (this.mode === 'ptt') {
      if (this.pressed) {
        out.push({ type: 'frame', frame, start });
        this.lastEnd = end;
      }
      return out;
    }
    if (!this.open) {
      if (vadActive) {
        this.open = true;
        const ring = keepFrom === undefined ? this.ring : this.ring.filter((r) => r.start + r.frame.length > keepFrom);
        const first = ring.length > 0 ? (ring[0] as { start: number }).start : start;
        out.push({ type: 'speech', state: 'start', sample: first });
        for (const r of ring) out.push({ type: 'frame', frame: r.frame, start: r.start });
        this.ring = [];
        out.push({ type: 'frame', frame, start });
        this.lastEnd = end;
      } else {
        this.ring.push({ frame, start });
        if (this.ring.length > this.preRollFrames) this.ring.shift();
      }
      return out;
    }
    if (vadActive) {
      out.push({ type: 'frame', frame, start });
      this.lastEnd = end;
    } else {
      this.open = false;
      out.push({ type: 'speech', state: 'end', sample: this.lastEnd });
      this.ring = [{ frame, start }];
    }
    return out;
  }

  pttDown(currentSample: number): GateAction[] {
    if (this.mode !== 'ptt' || this.pressed) return [];
    this.pressed = true;
    this.open = true;
    this.lastEnd = currentSample;
    return [{ type: 'speech', state: 'start', sample: currentSample }];
  }

  pttUp(tail: { frame: Int16Array; start: number } | null): GateAction[] {
    if (this.mode !== 'ptt' || !this.pressed) return [];
    this.pressed = false;
    this.open = false;
    const out: GateAction[] = [];
    if (tail) {
      out.push({ type: 'frame', frame: tail.frame, start: tail.start });
      this.lastEnd = tail.start + tail.frame.length;
    }
    out.push({ type: 'speech', state: 'end', sample: this.lastEnd });
    return out;
  }
}
